import { useEffect, useState, useMemo } from 'react';
import { FileText, Users, Loader2 } from 'lucide-react';
import SearchBar from '@/components/company/filters/SearchBar';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { toast } from 'react-toastify';
import useOrgChartUserState from '@/states/users.state';
import useTeamState from '@/states/team.state';
import useTenantState from '@/states/tenant.state';
import { DriveOrgMappingService } from '@/utils/api/drive-org-mapping';

interface OrgChartDetailsProps {
  selectedNode: {
    id: number;
    name?: string;
    description?: string;
  } | null;
}

interface SOPListProps {
  selectedNode: {
    id: number;
    name?: string;
    description?: string;
  };
}

function SOPList({ selectedNode }: SOPListProps) {
  const { currentTenant } = useTenantState();

  const { data: bpmnFiles = [], isLoading } = DriveOrgMappingService.useGetFilesByOrgChartNode(
    currentTenant?.id || 0,
    selectedNode.id,
    !!currentTenant?.id && !!selectedNode.id
  );

  // Sort alphabetically by name
  const sortedFiles = useMemo(() => {
    return [...bpmnFiles].sort((a, b) => a.name.localeCompare(b.name));
  }, [bpmnFiles]);

  let sopContent: JSX.Element;
  if (isLoading) {
    sopContent = (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
        <Loader2 size={24} className="animate-spin mb-2 text-primary" />
        <p className="text-sm">Loading SOPs...</p>
      </div>
    );
  } else if (sortedFiles.length > 0) {
    sopContent = (
      <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100 bg-white">
        {sortedFiles.map((file) => (
          <div key={file.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors">
            <FileText size={18} className="text-primary flex-shrink-0" />
            <span className="text-sm text-slate-700 font-medium">{file.name}</span>
          </div>
        ))}
      </div>
    );
  } else {
    sopContent = (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center border-2 border-dashed border-gray-100 rounded-lg">
        <FileText size={32} className="mb-2 opacity-20" />
        <p className="text-sm">No SOPs found for this role</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-700">Standard Operating Procedures</p>
      {sopContent}
    </div>
  );
}

function useDebounce<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function OrgChartDetails({ selectedNode }: OrgChartDetailsProps) {
  const [activeTab, setActiveTab] = useState<'sop' | 'teams'>('teams');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [showDropdown, setShowDropdown] = useState(false);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'name',
    order: 'asc',
  });

  const tenantId = useTenantState.getState().currentTenant?.id;
  const { users, fetchUsers, deleteUserFromNode, addUserToNode, isLoading: userLoading } = useOrgChartUserState();
  const { teamMembers, fetchTenantTeam, isLoading: teamLoading } = useTeamState();

  useEffect(() => {
    if (selectedNode?.id) fetchUsers(selectedNode.id);
  }, [selectedNode?.id]);

  useEffect(() => {
    if (!tenantId || !debouncedSearch.trim()) {
      setShowDropdown(false);
      return;
    }
    const controller = new AbortController();
    const fetchData = async () => {
      try {
        await fetchTenantTeam(tenantId, { ...pageConfig, search: debouncedSearch } as any);
        setShowDropdown(true);
      } catch (error: any) {
        if (error.name !== 'AbortError') toast.error('Failed to fetch team');
      }
    };
    fetchData();
    return () => controller.abort();
  }, [debouncedSearch, tenantId]);

  const handleSelectUser = async (email: string) => {
    if (!selectedNode?.id) return;
    await addUserToNode(selectedNode.id, email);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleDelete = async (row: any) => {
    if (!selectedNode?.id) return;
    await deleteUserFromNode(selectedNode.id, row.user_id);
  };

  const sortedUsers = [...users].sort((a: any, b: any) => {
    const dir = pageConfig.order === 'asc' ? 1 : -1;
    const aVal = a.email?.toLowerCase() ?? '';
    const bVal = b.email?.toLowerCase() ?? '';
    return aVal.localeCompare(bVal) * dir;
  });

  const columns: MainTableColumn[] = [
    { name: 'name', label: 'Name' },
    { name: 'email', label: 'Email' },
  ];

  if (!selectedNode) {
    return (
      <div className="w-full h-full bg-white border rounded-lg p-4 flex items-center justify-center text-gray-400 text-sm">
        Select a role from the organization structure to view details
      </div>
    );
  }

  return (
    <div className="h-full bg-white rounded-lg shadow-sm p-4 border space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-slate-800">{selectedNode.name}</h3>
      </div>

      <div className="border-b border-gray-200" />
      <div>
        <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
        <div
          className="prose prose-sm max-w-none text-slate-600 [&_p]:my-1"
          dangerouslySetInnerHTML={{
            __html: selectedNode.description || 'No description available.',
          }}
        />
      </div>

      <div className="border-b border-gray-200" />
      <div className="flex flex-col flex-1">
        <div className="flex gap-2 bg-gray-100 rounded-lg p-1 text-sm mb-2">
          <button
            onClick={() => setActiveTab('teams')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'teams' ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Users size={16} />
            Teams
          </button>
          <button
            onClick={() => setActiveTab('sop')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'sop' ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText size={16} />
            SOP
          </button>
        </div>

        {activeTab === 'sop' && (
          <SOPList selectedNode={selectedNode} />
        )}

        {activeTab === 'teams' && (
          <div className="space-y-3 relative">
            <p className="text-sm font-medium text-slate-700">Teams</p>
            <div className="relative">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search tenant users to add..."
                className="w-full mb-2"
              />
              {showDropdown && searchQuery && (
                <div className="absolute z-10 bg-white border rounded-lg shadow-md w-full mt-1 max-h-56 overflow-y-auto">
                  {teamLoading && <p className="p-3 text-sm text-gray-500">Loading...</p>}
                  {!teamLoading && teamMembers.length > 0 && teamMembers.map((member: any) => (
                    <button
                      key={member.id}
                      onClick={() => handleSelectUser(member.user?.email || member.email)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                    >
                      <div className="font-medium">{member.user?.name || member.name}</div>
                      <div className="text-xs text-gray-500">{member.user?.email || member.email}</div>
                    </button>
                  ))}
                  {!teamLoading && teamMembers.length === 0 && <p className="p-3 text-sm text-gray-500">No team found</p>}
                </div>
              )}
            </div>

            <div className="-mx-4 [&_section]:!pb-0">
              <MainTable
                showHeader={false}
                isLoading={userLoading}
                data={{
                  content: sortedUsers.map((u: any) => ({
                    ...u,
                    name: u.user?.name || '-',
                  })),
                  totalElements: sortedUsers.length,
                  totalPages: 1,
                }}
                columns={columns}
                pageConfig={pageConfig}
                setPageConfig={setPageConfig}
                showActions
                useConfirmDelete
                onDelete={handleDelete}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
